import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Clock, type LucideIcon } from "lucide-react";
import { useApi, useAuth, usePermission } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { Badge, CommandPalette as Palette, pushRecent as pushRecentEntry, readRecent, type PaletteGroup, type RecentEntry } from "@ncp/ui";
import { ALL_NAV_ITEMS, navAllowed } from "../app/nav";
import { useSurfaceLinks } from "../lib/links";

export { Shortcut } from "@ncp/ui";

/** A platform definition returned by `GET /api/platform/search`. */
export interface SearchHit {
  kind: "service" | "form" | "workflow" | "rule" | "automation" | "agent" | "connector" | "role" | "flag" | "template" | "changeSet" | "workspace" | "assistant" | "knowledge" | "recordType" | "report" | "screen" | "tool" | "record";
  key: string;
  name: string;
  nameAr?: string | null;
  status?: string | null;
  href: string;
}

const RECENT_KEY = "ncp.admin.recent";

export function pushRecent(entry: RecentEntry): void {
  pushRecentEntry(RECENT_KEY, entry);
}

export interface PaletteAction {
  id: string;
  label: string;
  hint?: string;
  icon?: LucideIcon;
  permission?: string;
  run: () => void;
}

/** Console palette: navigation, page actions, recent destinations and a server-side definition search. */
export function CommandPalette({ open, onOpenChange, actions = [] }: { open: boolean; onOpenChange: (open: boolean) => void; actions?: PaletteAction[] }) {
  const { t, i18n } = useTranslation();
  const { openInBackoffice, openInPortal } = useSurfaceLinks();
  const navigate = useNavigate();
  const api = useApi();
  const { clear } = useAuth();
  const { can } = usePermission();
  const [query, setQuery] = useState("");

  const q = query.trim();
  const search = useQuery({
    queryKey: ["platform", "search", q],
    queryFn: () => api.get<SearchHit[]>(`/api/platform/search?q=${encodeURIComponent(q)}`),
    enabled: open && q.length >= 2,
    staleTime: 10_000,
  });

  const ar = i18n.language.startsWith("ar");
  const go = (href: string, label: string) => {
    pushRecent({ href, label });
    navigate(href);
  };

  const groups = useMemo<PaletteGroup[]>(() => {
    const recent = q.length < 2 ? readRecent(RECENT_KEY) : [];
    const navItems = ALL_NAV_ITEMS.filter((i) => navAllowed(i, can));
    const visibleActions = actions.filter((a) => !a.permission || can(a.permission));
    return [
      {
        id: "recent",
        heading: t("admin:shell.groups.recent"),
        items: recent.map((r) => ({ id: r.href, label: r.label, keywords: r.href, icon: Clock, hint: r.href, onSelect: () => go(r.href, r.label) })),
      },
      {
        id: "nav",
        heading: t("admin:shell.groups.navigate"),
        items: [
          ...navItems.map((item) => ({
            id: item.key,
            label: t(`admin:nav.${item.label}`),
            keywords: item.href,
            icon: item.icon,
            keys: item.shortcut ? ["g", item.shortcut] : undefined,
            onSelect: () => go(item.href, t(`admin:nav.${item.label}`)),
          })),
          { id: "backoffice", label: t("admin:app.openBackoffice"), keywords: "backoffice case management inbox", icon: ArrowRight, onSelect: () => openInBackoffice("/") },
          { id: "portal", label: t("admin:app.openPortal"), keywords: "portal services applicant", icon: ArrowRight, onSelect: () => openInPortal("/") },
        ],
      },
      {
        id: "actions",
        heading: t("admin:shell.groups.actions"),
        items: visibleActions.length
          ? [
              ...visibleActions.map((a) => ({ id: a.id, label: a.label, keywords: a.hint, icon: a.icon, hint: a.hint, onSelect: a.run })),
              { id: "signout", label: t("admin:shell.signOut"), keywords: "sign out logout", icon: ArrowRight, onSelect: () => { clear(); navigate("/login"); } },
            ]
          : [],
      },
      {
        id: "definitions",
        heading: t("admin:shell.groups.definitions"),
        items:
          q.length >= 2
            ? (search.data ?? []).map((hit) => ({
                id: `${hit.kind}:${hit.key}`,
                label: ar && hit.nameAr ? hit.nameAr : hit.name,
                keywords: `${hit.kind} ${hit.key} ${hit.name} ${hit.nameAr ?? ""}`,
                leading: <Badge size="sm" variant="outline" className="w-24 justify-center font-mono text-[10px] uppercase">{hit.kind}</Badge>,
                hint: <span className="font-mono">{hit.key}</span>,
                trailing: hit.status ? <Badge size="sm" variant={hit.status === "Published" ? "success" : "outline"}>{hit.status}</Badge> : undefined,
                onSelect: () => go(hit.href, hit.name),
              }))
            : [],
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, actions, can, t, ar, search.data]);

  return <Palette open={open} onOpenChange={onOpenChange} groups={groups} query={query} onQueryChange={setQuery} label={t("admin:shell.commandPalette")} placeholder={t("admin:shell.commandHint")} emptyText={t("admin:shell.noResults")} />;
}
